const { isToday, isTomorrow, format } = require('date-fns');
const HRNumbers = require('human-readable-numbers');
const crypto = require('crypto');
const config = require('../config');
const appConstants = require('./constants/app');
const MOBILE_ROW_QUERIES = require('../data/mobileRowQuery.json');

const isClient = () => {
  return typeof window === 'object';
};

// A full reload, behind a function so tests can stub it: jsdom won't let a
// test replace window.location or its methods.
function reloadPage() {
  window.location.reload();
}

function isProdMode() {
  return config.mode === appConstants.MODE_PRODUCTION;
}

function isTestMode() {
  return config.mode === appConstants.MODE_TEST;
}

// Whether the page should hand its redux store out as `window.store`.
//
// The handle exists for the E2E suite: it is how a spec seeds a signed-in user
// and reads back the experiment arm the app chose, without standing up Firebase
// auth in a real browser (see tests/fixtures/stubbed.js). It has always existed
// in TEST, which is what the stubbed suite builds with.
//
// Every mode except production now gets it, because the payments suite has to
// build in DEV to be quoted the dev Stripe price ids, and a DEV build that lost
// the handle would break every existing spec instead (PROD-5374). Production
// never gets it: a page that hands out its own store is a debugging affordance,
// not a feature.
//
// `NEXT_PUBLIC_MODE` is inlined into the client bundle at build time, so this is
// settled once per build rather than per request.
function shouldExposeStoreHandle() {
  return !isProdMode() && isClient();
}

function getCookie(cname) {
  const name = `${cname}=`;
  if (typeof document === 'undefined') {
    return null;
  }
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

function generateQueryPath(pathname, query) {
  const path = `/${pathname}`;
  if (!query || typeof query !== 'object') {
    return path;
  }
  const queryParams = new URLSearchParams();
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (value !== null && value !== undefined && value !== '') {
      queryParams.set(key, value);
    }
  });
  const queryString = queryParams.toString();
  if (queryString && queryString.length) {
    return `${path}?${queryParams.toString()}`;
  }
  return path;
}

// Next's router hands back an array when a query param repeats in the URL
// (`?modelSource=a&modelSource=b`). Consumers expect the single comma-joined
// string `generateQueryPath` writes, and an array reaching string methods
// throws mid-render (PROD-6120). Join it back into that shape; anything else
// is returned untouched, so a normal value reaches the same code as before.
function joinRepeatedQueryValue(value) {
  return Array.isArray(value) ? value.join(',') : value;
}

function generateExternalUrlQueryPath(pathname, query) {
  const path = `${pathname}`;
  if (!query || typeof query !== 'object') {
    return path;
  }
  const queryParams = new URLSearchParams();
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (value !== null && value !== undefined && value !== '') {
      queryParams.set(key, value);
    }
  });
  const queryString = queryParams.toString();
  if (queryString && queryString.length) {
    return `${path}?${queryParams.toString()}`;
  }
  return path;
}

function getSortOrder(prop) {
  return (a, b) => {
    if (a[prop] < b[prop]) {
      return 1;
    }
    if (a[prop] > b[prop]) {
      return -1;
    }
    return 0;
  };
}

function formatSecondsAsTime(currentTime, options = {}) {
  let hr = Math.floor(currentTime / 3600);
  let min = Math.floor((currentTime - hr * 3600) / 60);
  let sec = Math.floor(currentTime - hr * 3600 - min * 60);
  if (options.isString) {
    if (hr < 1) {
      return `${min}m ${sec}s`;
    }
    return `${hr}h ${min}m ${sec}s`;
  }
  if (hr < 10 && hr > 0) {
    hr = `0${hr}`;
  }
  if (min < 10) {
    min = `0${min}`;
  }
  if (sec < 10) {
    sec = `0${sec}`;
  }
  if (hr > 0) {
    return `${hr}:${min}:${sec}`;
  }
  return `${min}:${sec}`;
}

function initialCapital(string) {
  if (typeof string === 'string' && string.length) {
    return string[0].toUpperCase() + string.slice(1);
  }

  return string;
}

function initialLowerCase(string) {
  if (typeof string === 'string' && string.length) {
    return string[0].toLowerCase() + string.slice(1);
  }
  return string;
}

function getCountDisplayValue(count) {
  if (!count || Number.isNaN(count)) return 0;
  if (count > 99 && count <= 999) {
    return `${HRNumbers.toHumanString(10 * Math.floor(count / 10))}+`;
  }
  if (count > 999) {
    return `${HRNumbers.toHumanString(100 * Math.floor(count / 100))}+`;
  }
  return count;
}

function getCountDisplayValueNumberOnly(count) {
  if (count > 99 && count <= 999) {
    return `${HRNumbers.toHumanString(10 * Math.floor(count / 10))}`;
  }
  if (count > 999) {
    return `${HRNumbers.toHumanString(100 * Math.floor(count / 100))}`;
  }
  return count;
}

function dateFormat(date) {
  if (isToday(new Date(date))) {
    return 'Today';
  }
  if (isTomorrow(new Date(date))) {
    return 'Tomorrow';
  }
  return format(new Date(date), 'EEE, MMM dd');
}

function getQueryFor(row) {
  if (!MOBILE_ROW_QUERIES[row] || !MOBILE_ROW_QUERIES[row].category) {
    return null;
  }
  if (MOBILE_ROW_QUERIES[row].category) {
    const res = {
      queryCategory: MOBILE_ROW_QUERIES[row].category,
      overrideDurationFilter: 4,
      useDayBucket: null,
      title: MOBILE_ROW_QUERIES[row].title,
    };
    return res;
  }
  return MOBILE_ROW_QUERIES[row].query;
}

function getSha1Hash(inputString, { lowercase = true } = {}) {
  if (!inputString || typeof inputString !== 'string') return '';
  let input = inputString;
  if (lowercase) {
    input = inputString.toLowerCase();
  }
  const shasum = crypto.createHash('sha1');
  shasum.update(input);
  return shasum.digest('hex');
}

const SESSISON_NAMES = {
  'free-discovery': 'Free Discovery',
  'paid-service-individual': 'Paid Service',
  'paid-coaching': 'Paid Coaching',
};

const getSessionsByMinutes = (packages, mins) => {
  return packages
    ?.filter(
      (item) =>
        item.features.find((itemConfig) => itemConfig.feature === 'video')
          .duration === mins
    )
    .sort(
      (a, b) => a.features[0].numberOfSessions - b.features[0].numberOfSessions
    );
};

const isAndroidDevice = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  const { userAgent } = window.navigator;
  if (!userAgent) return false;
  if (/android/i.test(userAgent)) {
    return true;
  }
  return false;
};

const isIosDevice = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  const { userAgent } = window.navigator;
  if (!userAgent) return false;
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true;
  }
  return false;
};

const generateMD5Hash = (inputString) => {
  if (!inputString) return '';
  return crypto.createHash('md5').update(inputString).digest('hex');
};

function convertToDollar(cent) {
  if (!cent) return 0;
  return cent / 100;
}

module.exports = {
  isClient,
  reloadPage,
  isProdMode,
  isTestMode,
  shouldExposeStoreHandle,
  isAndroidDevice,
  isIosDevice,
  getCookie,
  generateQueryPath,
  joinRepeatedQueryValue,
  getSortOrder,
  generateExternalUrlQueryPath,
  formatSecondsAsTime,
  initialCapital,
  getCountDisplayValue,
  dateFormat,
  getQueryFor,
  getCountDisplayValueNumberOnly,
  SESSISON_NAMES,
  getSessionsByMinutes,
  getSha1Hash,
  generateMD5Hash,
  initialLowerCase,
  convertToDollar,
};
